import {
  Children,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import styled, { css, keyframes } from 'styled-components';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const itemWidth = 185;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  min-height: 128px;
  background: rgba(var(--c-rgb-black), 0.4);
`;

const LoadingIndicatorContainer = styled.div`
  position: sticky;
  top: 50%;
  transform: translateY(-50%);
`;

export const GridOverlayLoadingIndicator = () => {
  return (
    <LoadingOverlay>
      <LoadingIndicatorContainer>
        <LoadingIndicator />
      </LoadingIndicatorContainer>
    </LoadingOverlay>
  );
};
const appearIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Grid = styled.div<{ columns?: number }>`
  display: grid;
  gap: var(--double-unit);
  grid-template-columns: 1fr;
  /* only for mobile */
  &.twoColumnsOnMobile {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${tabletAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(${itemWidth}px, 1fr));

    /* the following rules are for where the grid is full width */
    /* it ensures that each line has the same number of cards regardless of the screen size */
    ${props =>
      props.columns &&
      css`
        grid-template-columns: repeat(${props.columns}, minmax(0, 1fr));
      `}
    > * {
      animation: 0.3s ${appearIn} ease-in-out forwards;
    }
  }
`;
const extractKeys = (children: ReactNode) => {
  try {
    return Children.toArray(children)
      ?.flatMap(async props => props)
      .map(
        async props =>
          await (props !== null && typeof props === 'object' && 'key' in props
            ? props.key
            : '')
      )
      .filter(Boolean)
      .sort()
      .join(',');
  } catch {
    return '';
  }
};

type Props = { columns?: number; animate?: boolean; className?: string };
export const AnimatedGrid = ({
  columns,
  children,
  animate,
  className,
}: PropsWithChildren<Props>) => {
  const [ids, setIds] = useState(extractKeys(children));
  const [newChildren, setNewChildren] = useState(children);
  const { updateDOM } = useTransitionApi();
  const {
    flags: { enableNewGridAnimation = false },
  } = useFeatureFlags();

  useEffect(() => {
    const newIds = extractKeys(children);
    if (ids !== newIds) {
      updateDOM(() => {
        setIds(newIds);
        setNewChildren(children);
      });
    }
  });

  if (enableNewGridAnimation && animate) {
    return (
      <Grid columns={columns} className={className}>
        {Children.toArray(newChildren).flatMap(c => {
          return (
            c && (
              <div
                style={{
                  viewTransitionName:
                    typeof c === 'object' && 'key' in c
                      ? c.key?.replace(/[^A-Za-z-]/g, '')
                      : undefined, // sanitize c.key to be compatible with viewTransitionName requirement
                  // @ts-expect-error not included in react types yet
                  viewTransitionClass: 'grid-card',
                }}
              >
                {c}
              </div>
            )
          );
        })}
      </Grid>
    );
  }
  return (
    <Grid className={className} columns={columns}>
      {children}
    </Grid>
  );
};
